// import packages
import React from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Redirect, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

// import apis

// import redux, selectors, actions

// import contexts
import { AuthContext } from 'contexts/AuthContext'

// import hooks

// import utilities
import { publicFetch } from 'helpers/fetch'

// import common elements
import { Form, Button, Spinner, FloatingLabel } from 'react-bootstrap'

// import components
import { Focus } from 'components/elements/Layout'
import PublicNav from 'components/navs/PublicNav'

// import styles

// local constants

const FIELDS = [
  { name: 'first_name', type: 'text', label: 'First name' },
  { name: 'last_name', type: 'text', label: 'Last name' },
  { name: 'email', type: 'text', label: 'Email' },
  { name: 'password', type: 'text', label: 'Password' },
]

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
}

const SignupSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, 'Must have at least 2 characters')
    .max(100, 'Cannot longer than 100 characters')
    .required('First name is required'),
  last_name: Yup.string()
    .min(2, 'Must have at least 2 characters')
    .max(100, 'Cannot longer than 100 characters')
    .required('Last name is required'),
  email: Yup.string()
    .email('Invalid email')
    .max(100, 'Cannot longer than 100 characters')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
})

// --------------------

const Signup = () => {
  const history = useHistory()

  const authContext = React.useContext(AuthContext)
  const [redirectOnLogin, setRedirectOnLogin] = React.useState(false)
  const [loginLoading, setLoginLoading] = React.useState(false)

  React.useEffect(() => {
    window.scroll(0, 0)
  }, [])

  const switchToLogin = () => {
    history.replace('/login')
  }

  const switchToForgotPassword = () => {
    history.replace('/forgot-password')
  }

  const links = [
    ['Forgot password?', switchToForgotPassword],
    ['Have an account? Log in', switchToLogin],
  ]

  const submitCredentials = async (credentials, setErrors) => {
    try {
      setLoginLoading(true)
      const { data } = await publicFetch.post('register', credentials)

      authContext.setAuthState(data)
      toast.success(data.message)

      setTimeout(() => {
        setRedirectOnLogin(true)
      }, 700)
    } catch (error) {
      setLoginLoading(false)
      if (error.response) {
        const { data } = error.response
        if (data.message) {
          toast.error(data.message)
        } else if (error.message) {
          toast.error(error.message)
        }
        const fieldErrors = data.errors ? data.errors : {}
        setErrors({ ...INITIAL_VALUES, ...fieldErrors })
      } else if (error.message) {
        toast.error(error.message)
      } else {
        toast.error('Unknown error')
      }
    }
  }

  return (
    <>
      {redirectOnLogin && <Redirect to='/' />}
      <Focus icon='register' title='Sign up for an account' links={links}>
        <PublicNav />
        <Formik
          initialValues={INITIAL_VALUES}
          onSubmit={(
            values,
            {
              authError,
              setError,
              setErrors,
              setSubmitting,
              resetForm,
              setFieldError,
            },
          ) => submitCredentials(values, setErrors)}
          validationSchema={SignupSchema}
          validateOnBlur={false}
        >
          {({
            values,
            errors,
            touched,
            isValid,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => {
            const input = field => {
              return (
                <Form.Group
                  className='mt-4'
                  key={field.name}
                  controlId={field.name}
                >
                  <FloatingLabel controlId={field.name} label={field.label}>
                    <Form.Control
                      name={field.name}
                      type={field.type}
                      placeholder=' '
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values[field.name]}
                      isInvalid={touched[field.name] && !!errors[field.name]}
                    />
                    <Form.Control.Feedback type='invalid'>
                      {errors[field.name]}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              )
            }

            return (
              <Form noValidate onSubmit={handleSubmit}>
                {FIELDS.map(input)}
                <div className='d-grid my-3 '>
                  <Button
                    type='submit'
                    variant='primary'
                    disabled={loginLoading}
                  >
                    {loginLoading && <Spinner />}
                    Sign up
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      </Focus>
    </>
  )
}

export default Signup
